import { useState, useCallback, useMemo } from "react";

export default function useSelection(items, idKey = "id") {
  const [selected, setSelected] = useState(() => new Set());

  const isSelected = useCallback(
    (id) => selected.has(id),
    [selected]
  );

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const ids = useMemo(
    () => (items || []).map((it) => it[idKey]),
    [items, idKey]
  );

  const allSelected = useMemo(
    () => ids.length > 0 && ids.every((id) => selected.has(id)),
    [ids, selected]
  );

  const someSelected = useMemo(
    () => ids.some((id) => selected.has(id)) && !allSelected,
    [ids, selected, allSelected]
  );

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (ids.length > 0 && ids.every((id) => prev.has(id))) {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, [ids]);

  return {
    selected,
    selectedIds: Array.from(selected),
    count: selected.size,
    isSelected,
    toggle,
    toggleAll,
    clear,
    allSelected,
    someSelected,
  };
}
